// Versioned actuarial assumption service for DB / cash-balance modeling.
//
// One registry of assumption SETS, each carrying the annuity purchase rate
// (APR) tables the DB conversions need — present value at a given age of a
// $1/month annuity — on four bases:
//
//   plan                   1994 GAR Unisex projected to 2002 at 5% — the
//                          actuarial-equivalence basis a typical cash-balance
//                          plan adopts (account → annuity at retirement age).
//   testing                UP-1984 at 8.5% — the standard-assumption basis for
//                          §401(a)(4) equivalent-benefit testing.
//   applicableMortality5   Applicable mortality at 5% — used in the §415(b)
//                          lesser-of limit machinery.
//   lumpSum55              Applicable-mortality-style basis at 5.5% — used for
//                          the maximum-contribution conversion.
//
// PROVENANCE, not derivation: every tabled value was transcribed from a
// professionally produced 2026 actuarial proposal and cross-checked
// arithmetically against that proposal's own limit and maximum-contribution
// columns from the verified reference scenarios. We deliberately do NOT compute
// factors from raw mortality rates we cannot source. Ages between anchors are
// linearly interpolated and labeled `interpolated`; ages outside the anchored
// span return null so callers surface "unavailable" instead of an invented
// number. Every lookup reports its provenance so results can disclose exactly
// which factors were verified and which were filled in.
//
// Unsupported set ids are null — never silently replaced by a default.

const PLAN_LIFE = {
  19: 231.53, 25: 227.06, 26: 226.20, 27: 225.30, 28: 224.36, 29: 223.38,
  31: 221.28, 32: 220.15, 33: 218.98, 38: 212.20, 40: 209.04, 41: 207.35,
  46: 197.77, 47: 195.60, 52: 183.36, 54: 177.81, 56: 171.87, 59: 162.34,
  62: 152.1580, 65: 141.5305, 66: 137.92, 67: 134.29, 68: 130.63, 69: 126.90,
}

const PLAN_JOINT = {
  19: 234.47, 25: 230.70, 26: 229.97, 27: 229.21, 28: 228.40, 29: 227.57,
  31: 225.77, 32: 224.81, 33: 223.80, 38: 217.98, 40: 215.26, 41: 213.80,
  46: 205.47, 47: 203.57, 52: 192.79, 54: 187.84, 56: 182.52, 59: 173.84,
  65: 154.31,
}

const TESTING_LIFE = {
  65: 95.3851,
  69: 86.3768,
}

const TESTING_JOINT = {
  19: 144.95, 25: 143.95, 26: 143.72, 27: 143.47, 28: 143.20, 29: 142.90,
  31: 142.23, 32: 141.86, 33: 141.45, 38: 138.96, 40: 137.72, 41: 137.03,
  46: 132.96, 47: 132.00, 52: 126.40, 54: 123.77, 56: 120.88, 59: 116.07,
  65: 104.68, 69: 104.68 * (86.3768 / 95.3851), // scaled with the life ratio; provenance 'estimated'
}

const APPL_MORT_5_LIFE = {
  19: 234.05, 25: 230.20, 26: 229.45, 27: 228.66, 28: 227.84, 29: 226.97,
  31: 225.12, 32: 224.14, 33: 223.10, 38: 217.18, 40: 214.41, 41: 212.92,
  46: 204.36, 47: 202.39, 52: 191.14, 54: 185.93, 56: 180.32, 59: 171.27,
  62: 161.40, 65: 150.88,
}

const LUMP_SUM_55_LIFE = {
  19: 215.92, 25: 212.96, 26: 212.37, 27: 211.75, 28: 211.11, 29: 210.43,
  31: 208.95, 32: 208.16, 33: 207.33, 38: 202.52, 40: 200.24, 41: 199.00,
  46: 191.79, 47: 190.12, 52: 180.40, 54: 175.84, 56: 170.89, 59: 162.83,
  65: 144.42,
}

// Ages whose values were NOT directly printed in the reference proposal.
const ESTIMATED_ANCHOR_AGES = { 'testing:joint': new Set([69]) }

export const EBG_2026_ACTUARIAL_SET = 'ebg-2026-proposal-v1'

const ASSUMPTION_SETS = Object.freeze({
  [EBG_2026_ACTUARIAL_SET]: Object.freeze({
    id: EBG_2026_ACTUARIAL_SET,
    label: 'EBG 2026 proposal-verified APR tables',
    effectiveVersion: 1,
    factorSource: 'Transcribed from a professionally produced 2026 actuarial proposal and cross-checked against its own worksheets; confirm the plan’s adopted actuarial-equivalence basis.',
    status: 'scenario-estimate',
    descriptions: Object.freeze({
      plan: '1994 GAR Unisex projected to 2002 at 5% (plan actuarial equivalence)',
      testing: 'UP-1984 at 8.5% (§401(a)(4) standard testing basis)',
      applicableMortality5: 'Applicable mortality at 5% (§415(b) limit basis)',
      lumpSum55: 'Applicable-mortality basis at 5.5% (maximum-contribution conversion)',
    }),
    tables: Object.freeze({
      plan: { life: Object.freeze(PLAN_LIFE), joint: Object.freeze(PLAN_JOINT) },
      testing: { life: Object.freeze(TESTING_LIFE), joint: Object.freeze(TESTING_JOINT) },
      applicableMortality5: { life: Object.freeze(APPL_MORT_5_LIFE) },
      lumpSum55: { life: Object.freeze(LUMP_SUM_55_LIFE) },
    }),
  }),
})

export const DEFAULT_ACTUARIAL_SET = EBG_2026_ACTUARIAL_SET

export function getActuarialAssumptionSet(id = DEFAULT_ACTUARIAL_SET) {
  return ASSUMPTION_SETS[id] || null
}

export function listActuarialAssumptionSets() {
  return Object.values(ASSUMPTION_SETS)
}

export function actuarialBasisDescription(basis, setId = DEFAULT_ACTUARIAL_SET) {
  return getActuarialAssumptionSet(setId)?.descriptions?.[basis] ?? null
}

// Look up an APR with provenance. Returns { value, provenance } or null when
// the set is unknown or the age falls outside the anchored span — callers
// must surface that as "unavailable", never substitute a guess.
export function annuityFactor({ basis, form = 'life', age, customTable, assumptionSet = DEFAULT_ACTUARIAL_SET } = {}) {
  const set = getActuarialAssumptionSet(assumptionSet)
  const table = customTable || set?.tables?.[basis]?.[form] || null
  if (!table) return null
  const target = Math.round(Number(age))
  if (!Number.isFinite(target)) return null
  const exact = table[target]
  if (Number.isFinite(exact) && exact > 0) {
    const estimated = !customTable && ESTIMATED_ANCHOR_AGES[`${basis}:${form}`]?.has(target)
    return { value: exact, provenance: customTable ? 'custom' : (estimated ? 'estimated' : 'verified') }
  }
  const ages = Object.keys(table).map(Number).sort((a, b) => a - b)
  if (!ages.length || target < ages[0] || target > ages[ages.length - 1]) return null
  let lower = ages[0]
  let upper = ages[ages.length - 1]
  for (const anchor of ages) {
    if (anchor < target) lower = anchor
    if (anchor > target) { upper = anchor; break }
  }
  const fraction = (target - lower) / (upper - lower)
  const value = table[lower] + (table[upper] - table[lower]) * fraction
  return { value, provenance: customTable ? 'custom-interpolated' : 'interpolated' }
}

// De-duplicate and sort a factor-usage log for disclosure blocks.
export function summarizeFactorsUsed(factors = []) {
  const seen = new Map()
  for (const factor of factors) {
    seen.set(`${factor.basis}:${factor.form}:${factor.age}`, factor)
  }
  return [...seen.values()]
    .sort((a, b) => a.basis.localeCompare(b.basis) || a.form.localeCompare(b.form) || a.age - b.age)
}
